import axios from 'axios';

/**
 * Smart Search Service
 * Provides intelligent search across features, actions, and help content
 */
class SmartSearchService {
  constructor() {
    this.searchHistory = [];
    this.maxHistorySize = 10;
    this.recentSearches = this.loadRecentSearches();
  }

  /**
   * Perform intelligent search
   */
  async search(query, context = {}) {
    if (!query || query.trim().length === 0) {
      return this.getEmptyResults();
    }

    try {
      const response = await axios.post('/api/ai/search', {
        query: query.trim(),
        context,
      });

      const results = response.data.data;

      // Add to search history
      this.addToHistory(query, results);

      return {
        success: true,
        ...results,
      };

    } catch (error) {
      console.error('[SmartSearch] Search failed:', error);
      
      // Fallback to local search
      return this.fallbackLocalSearch(query);
    }
  }

  /**
   * Get popular/recent searches
   */
  getRecentSearches() {
    return this.recentSearches.slice(0, 5);
  }

  /**
   * Get trending searches (mock for now)
   */
  getTrendingSearches() {
    return [
      'Add HCP',
      'Call Reports',
      'Schedule Visit',
      'Submit Expense',
      'Dashboard',
    ];
  }

  /**
   * Get quick actions (shortcuts)
   */
  getQuickActions() {
    return [
      {
        label: 'Add New HCP',
        icon: 'cil-user-plus',
        route: '/hcp/create',
        type: 'navigate',
        category: 'Create',
      },
      {
        label: 'Submit Call Report',
        icon: 'cil-note-add',
        route: '/calls/create',
        type: 'navigate',
        category: 'Create',
      },
      {
        label: 'View Calendar',
        icon: 'cil-calendar',
        route: '/calendar',
        type: 'navigate',
        category: 'View',
      },
      {
        label: 'My Tasks',
        icon: 'cil-task',
        route: '/tasks',
        type: 'navigate',
        category: 'View',
      },
      {
        label: 'Dashboard',
        icon: 'cil-speedometer',
        route: '/dashboard',
        type: 'navigate',
        category: 'View',
      },
      {
        label: 'Reports',
        icon: 'cil-chart',
        route: '/reports',
        type: 'navigate',
        category: 'View',
      },
    ];
  }

  /**
   * Add search to history
   */
  addToHistory(query, results) {
    const historyItem = {
      query,
      timestamp: new Date().toISOString(),
      resultsCount: (results.features?.length || 0) + 
                   (results.actions?.length || 0) + 
                   (results.help_articles?.length || 0),
    };

    this.searchHistory.unshift(historyItem);

    if (this.searchHistory.length > this.maxHistorySize) {
      this.searchHistory.pop();
    }

    // Update recent searches
    if (!this.recentSearches.includes(query)) {
      this.recentSearches.unshift(query);
      if (this.recentSearches.length > this.maxHistorySize) {
        this.recentSearches.pop();
      }
      this.saveRecentSearches();
    }
  }

  /**
   * Clear search history
   */
  clearHistory() {
    this.searchHistory = [];
    this.recentSearches = [];
    this.saveRecentSearches();
  }

  /**
   * Load recent searches from localStorage
   */
  loadRecentSearches() {
    try {
      const stored = localStorage.getItem('smartSearchHistory');
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      return [];
    }
  }

  /**
   * Save recent searches to localStorage
   */
  saveRecentSearches() {
    try {
      localStorage.setItem('smartSearchHistory', JSON.stringify(this.recentSearches));
    } catch (error) {
      console.warn('[SmartSearch] Failed to save search history:', error);
    }
  }

  /**
   * Get empty results structure
   */
  getEmptyResults() {
    return {
      success: true,
      query: '',
      features: [],
      actions: [],
      help_articles: [],
      suggestions: [],
      quick_actions: this.getQuickActions(),
    };
  }

  /**
   * Fallback local search when API fails
   */
  fallbackLocalSearch(query) {
    const features = this.getLocalFeatures();
    const actions = this.getLocalActions();
    
    const queryLower = query.toLowerCase();

    const matchedFeatures = features.filter(f => 
      f.name.toLowerCase().includes(queryLower) || 
      f.description.toLowerCase().includes(queryLower) ||
      f.keywords.some(k => k.includes(queryLower))
    ).slice(0, 5);

    const matchedActions = actions.filter(a => 
      a.name.toLowerCase().includes(queryLower) || 
      a.description.toLowerCase().includes(queryLower)
    ).slice(0, 5);

    return {
      success: true,
      query,
      features: matchedFeatures,
      actions: matchedActions,
      help_articles: [],
      suggestions: [],
      quick_actions: [],
    };
  }

  /**
   * Get local feature registry (fallback)
   */
  getLocalFeatures() {
    return [
      {
        type: 'feature',
        name: 'HCP Management',
        description: 'Manage healthcare professionals and doctors',
        icon: 'cil-people',
        route: '/hcp',
        category: 'Customers',
        keywords: ['doctor', 'physician', 'hcp', 'contacts'],
      },
      {
        type: 'feature',
        name: 'Call Reports',
        description: 'Track sales visits and meetings',
        icon: 'cil-phone',
        route: '/calls',
        category: 'Activities',
        keywords: ['visit', 'meeting', 'call', 'activity'],
      },
      {
        type: 'feature',
        name: 'Schedule',
        description: 'View and manage your calendar',
        icon: 'cil-calendar',
        route: '/calendar',
        category: 'Planning',
        keywords: ['calendar', 'schedule', 'appointment'],
      },
      {
        type: 'feature',
        name: 'Tasks',
        description: 'Manage your to-do list',
        icon: 'cil-task',
        route: '/tasks',
        category: 'Productivity',
        keywords: ['todo', 'task', 'checklist'],
      },
      {
        type: 'feature',
        name: 'Expenses',
        description: 'Submit and manage expense reports',
        icon: 'cil-dollar',
        route: '/expenses',
        category: 'Finance',
        keywords: ['expense', 'cost', 'reimbursement'],
      },
      {
        type: 'feature',
        name: 'Dashboard',
        description: 'View performance metrics and analytics',
        icon: 'cil-speedometer',
        route: '/dashboard',
        category: 'Analytics',
        keywords: ['home', 'overview', 'metrics', 'stats'],
      },
    ];
  }

  /**
   * Get local actions registry (fallback)
   */
  getLocalActions() {
    return [
      {
        type: 'action',
        name: 'Add HCP',
        description: 'Create a new healthcare professional record',
        icon: 'cil-user-plus',
        route: '/hcp/create',
        action: 'create_hcp',
      },
      {
        type: 'action',
        name: 'Submit Call Report',
        description: 'Log a new sales call or visit',
        icon: 'cil-note-add',
        route: '/calls/create',
        action: 'create_call',
      },
      {
        type: 'action',
        name: 'Add Expense',
        description: 'Submit a new expense report',
        icon: 'cil-plus',
        route: '/expenses/create',
        action: 'create_expense',
      },
      {
        type: 'action',
        name: 'Schedule Visit',
        description: 'Plan a new HCP visit',
        icon: 'cil-calendar-check',
        route: '/calendar/create',
        action: 'schedule_visit',
      },
    ];
  }
}

// Create singleton instance
const smartSearchService = new SmartSearchService();

export default smartSearchService;

// Named export for testing
export { SmartSearchService };
